/**
 * Hindi and English, Hindi first (§13.7).
 *
 * ┌─ WHY HINDI IS THE DEFAULT, NOT AN OPTION ─────────────────────────────┐
 * │ The first vendors are printers, installers, and hoarding owners in     │
 * │ Meerut, Ghaziabad, and Lucknow. An English-first portal with a Hindi   │
 * │ toggle buried in settings is an English-only portal in practice,       │
 * │ because the person who needs the toggle cannot read the path to it.    │
 * └────────────────────────────────────────────────────────────────────────┘
 *
 * A hand-written dictionary rather than an i18n library: there are two
 * languages and about a hundred strings, and every one of them needs to be
 * read by someone who knows the trade vocabulary. `flex`, `challan`, and
 * `hoarding` stay in Devanagari transliteration because that is what vendors
 * actually say — translating `challan` to `रसीद` would be more correct and
 * less understood.
 */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

export type Lang = 'hi' | 'en';

const STORAGE_KEY = 'lulu_partner_lang';

const STRINGS = {
  // --- shell -------------------------------------------------------------
  'app.name': { hi: 'लुलु पार्टनर', en: 'Lulu Partner' },
  'nav.jobs': { hi: 'काम', en: 'Jobs' },
  'nav.listings': { hi: 'साइट', en: 'Listings' },
  'nav.payouts': { hi: 'पैसा', en: 'Payments' },
  'nav.profile': { hi: 'प्रोफ़ाइल', en: 'Profile' },
  'nav.creatorHome': { hi: 'होम', en: 'Home' },
  'nav.mediaKit': { hi: 'मीडिया किट', en: 'Media kit' },
  'nav.packages': { hi: 'पैकेज', en: 'Packages' },
  'nav.deals': { hi: 'डील', en: 'Deals' },
  'nav.earnings': { hi: 'कमाई', en: 'Earnings' },
  'nav.creator': { hi: 'क्रिएटर', en: 'Creator' },
  'common.back': { hi: 'वापस', en: 'Back' },
  'common.cancel': { hi: 'रद्द करें', en: 'Cancel' },
  'common.save': { hi: 'सेव करें', en: 'Save' },
  'common.retry': { hi: 'दोबारा कोशिश करें', en: 'Try again' },
  'common.loading': { hi: 'लोड हो रहा है…', en: 'Loading…' },
  'common.days': { hi: 'दिन', en: 'days' },
  'common.optional': { hi: 'ज़रूरी नहीं', en: 'optional' },
  'common.somethingWrong': {
    hi: 'कुछ गड़बड़ हुई। दोबारा कोशिश करें।',
    en: 'Something went wrong. Try again.',
  },

  // --- sign in -----------------------------------------------------------
  'signin.title': { hi: 'अपना काम देखें', en: 'See your work' },
  'signin.subtitle': {
    hi: 'मोबाइल नंबर डालें। हम WhatsApp पर एक कोड भेजेंगे।',
    en: 'Enter your mobile number. We will send a code on WhatsApp.',
  },
  'signin.phone': { hi: 'मोबाइल नंबर', en: 'Mobile number' },
  'signin.sendCode': { hi: 'कोड भेजें', en: 'Send code' },
  'signin.sending': { hi: 'भेजा जा रहा है…', en: 'Sending…' },
  'signin.codeTitle': { hi: 'कोड डालें', en: 'Enter the code' },
  'signin.codeSentTo': { hi: 'WhatsApp पर कोड भेजा गया', en: 'Code sent on WhatsApp to' },
  'signin.code': { hi: '6 अंकों का कोड', en: '6-digit code' },
  'signin.verify': { hi: 'आगे बढ़ें', en: 'Continue' },
  'signin.changeNumber': { hi: 'नंबर बदलें', en: 'Change number' },
  'signin.resend': { hi: 'दोबारा भेजें', en: 'Send again' },
  'signin.devCode': {
    hi: 'SMS अभी चालू नहीं है — कोड यहाँ दिख रहा है',
    en: 'SMS is not live yet — the code is shown here',
  },

  // --- registration ------------------------------------------------------
  'register.title': { hi: 'अपना काम रजिस्टर करें', en: 'Register your business' },
  'register.subtitle': {
    hi: 'एक बार भरें। फिर काम आना शुरू।',
    en: 'Fill this once. Then work starts arriving.',
  },
  'register.businessName': { hi: 'दुकान / फर्म का नाम', en: 'Business name' },
  'register.contactName': { hi: 'आपका नाम', en: 'Your name' },
  'register.city': { hi: 'शहर', en: 'City' },
  'register.state': { hi: 'राज्य', en: 'State' },
  'register.capabilities': { hi: 'आप क्या काम करते हैं?', en: 'What work do you do?' },
  'register.capabilitiesHint': {
    hi: 'जितने चाहें चुनें। इसी हिसाब से काम भेजा जाएगा।',
    en: 'Pick as many as apply. This is what we send you.',
  },
  'register.kyc': {
    hi: 'कागज़ (बाद में भी भर सकते हैं)',
    en: 'Documents (can be added later)',
  },
  'register.pan': { hi: 'PAN', en: 'PAN' },
  'register.gstin': { hi: 'GSTIN', en: 'GSTIN' },
  'register.panWarning': {
    hi: 'PAN के बिना हर पेमेंट पर 20% TDS कटेगा, 1-2% की जगह।',
    en: 'Without a PAN, every payment is docked 20% TDS instead of 1-2%.',
  },
  'register.submit': { hi: 'रजिस्टर करें', en: 'Register' },
  'register.submitting': { hi: 'भेजा जा रहा है…', en: 'Submitting…' },
  'register.pendingTitle': { hi: 'रजिस्टर हो गया', en: 'You are registered' },
  'register.pendingBody': {
    hi: 'हमारी टीम आपके कागज़ देखकर मंज़ूरी देगी। उसके बाद काम आना शुरू हो जाएगा।',
    en: 'Our team will check your details and approve you. Work starts arriving after that.',
  },

  // --- jobs --------------------------------------------------------------
  'jobs.title': { hi: 'आपका काम', en: 'Your work' },
  'jobs.newRequests': { hi: 'नए काम', en: 'New requests' },
  'jobs.active': { hi: 'चल रहे काम', en: 'In progress' },
  'jobs.emptyTitle': { hi: 'अभी कोई काम नहीं', en: 'Nothing right now' },
  'jobs.emptyBody': {
    hi: 'जैसे ही आपके शहर में आपके काम का ऑर्डर आएगा, यहाँ दिखेगा।',
    en: 'When a job in your line comes up in your city, it appears here.',
  },
  'jobs.pendingTitle': { hi: 'मंज़ूरी का इंतज़ार', en: 'Waiting for approval' },
  'jobs.pendingBody': {
    hi: 'मंज़ूरी मिलते ही काम आना शुरू हो जाएगा।',
    en: 'Work starts arriving as soon as you are approved.',
  },
  'jobs.neededBy': { hi: 'चाहिए', en: 'Needed by' },
  'jobs.closesIn': { hi: 'में बंद', en: 'closes in' },
  'jobs.closed': { hi: 'बंद हो गया', en: 'closed' },
  'jobs.quote': { hi: 'रेट भेजें', en: 'Send a quote' },
  'jobs.quoted': { hi: 'रेट भेजा गया', en: 'Quote sent' },
  'jobs.decline': { hi: 'नहीं कर सकते', en: 'Cannot do this' },
  'jobs.declined': { hi: 'मना कर दिया', en: 'Declined' },
  'jobs.start': { hi: 'काम शुरू किया', en: 'Started this job' },
  'jobs.uploadProof': { hi: 'फ़ोटो / रसीद भेजें', en: 'Send proof' },
  'jobs.awaitingVerify': { hi: 'ग्राहक की जाँच बाकी', en: 'Waiting for the customer to check' },
  'jobs.spec': { hi: 'क्या बनाना है', en: 'What is needed' },
  'jobs.proofNeeded': { hi: 'क्या भेजना होगा', en: 'Proof required' },
  'jobs.awarded': { hi: 'आपको मिला', en: 'Awarded to you' },

  // --- quote form --------------------------------------------------------
  'quote.title': { hi: 'अपना रेट भेजें', en: 'Send your quote' },
  'quote.amount': { hi: 'कुल रेट (₹)', en: 'Your price (₹)' },
  'quote.amountHint': { hi: 'GST अलग से', en: 'Excluding GST' },
  'quote.days': { hi: 'कितने दिन लगेंगे?', en: 'How many days?' },
  'quote.note': { hi: 'कुछ कहना है?', en: 'Anything to add?' },
  'quote.notePlaceholder': {
    hi: 'जैसे: 440gsm फ्लेक्स, आइलेट के साथ',
    en: 'e.g. 440gsm flex with eyelets',
  },
  'quote.submit': { hi: 'रेट भेजें', en: 'Send quote' },
  'quote.sending': { hi: 'भेजा जा रहा है…', en: 'Sending…' },
  'quote.sent': { hi: 'रेट भेज दिया गया', en: 'Quote sent' },
  'quote.won': { hi: 'यह काम आपको मिल गया!', en: 'You have this job!' },

  // --- proof -------------------------------------------------------------
  'proof.title': { hi: 'काम का सबूत भेजें', en: 'Send proof of work' },
  'proof.takePhoto': { hi: 'फ़ोटो लें', en: 'Take a photo' },
  'proof.chooseFile': { hi: 'फ़ाइल चुनें', en: 'Choose a file' },
  'proof.gpsNeeded': {
    hi: 'लगाने के काम में फ़ोटो साइट पर ही लेनी है — लोकेशन चालू रखें।',
    en: 'For installation, take the photo at the site with location switched on.',
  },
  'proof.gettingLocation': { hi: 'लोकेशन ली जा रही है…', en: 'Getting location…' },
  'proof.locationOk': { hi: 'लोकेशन मिल गई', en: 'Location captured' },
  'proof.locationFailed': {
    hi: 'लोकेशन नहीं मिली। फ़ोन की सेटिंग में लोकेशन चालू करें।',
    en: 'Could not get the location. Switch it on in your phone settings.',
  },
  'proof.compressing': { hi: 'फ़ोटो छोटी की जा रही है…', en: 'Shrinking the photo…' },
  'proof.uploading': { hi: 'भेजा जा रहा है…', en: 'Sending…' },
  'proof.send': { hi: 'भेजें', en: 'Send' },
  'proof.sent': { hi: 'भेज दिया गया', en: 'Sent' },
  'proof.paidAfter': {
    hi: 'ग्राहक के जाँचने के बाद पेमेंट चालू हो जाएगी।',
    en: 'Payment is released once the customer checks the work.',
  },

  // --- payouts -----------------------------------------------------------
  'payouts.title': { hi: 'आपका पैसा', en: 'Your payments' },
  'payouts.pending': { hi: 'आना बाकी', en: 'Coming to you' },
  'payouts.empty': { hi: 'अभी कोई पेमेंट नहीं', en: 'No payments yet' },
  'payouts.gross': { hi: 'कुल रेट', en: 'Job total' },
  'payouts.commission': { hi: 'लुलु कमीशन', en: 'Lulu commission' },
  'payouts.tds': { hi: 'TDS (सरकार को)', en: 'TDS (to the government)' },
  'payouts.net': { hi: 'आपको मिलेगा', en: 'You receive' },
  'payouts.due': { hi: 'तारीख', en: 'Due' },
  'payouts.reference': { hi: 'रेफ़रेंस', en: 'Reference' },
  'payouts.status.pending': { hi: 'जाँच बाकी', en: 'Awaiting sign-off' },
  'payouts.status.approved': { hi: 'मंज़ूर', en: 'Approved' },
  'payouts.status.paid': { hi: 'भेज दिया', en: 'Paid' },
  'payouts.status.held': { hi: 'रोका गया', en: 'On hold' },
  'payouts.status.disputed': { hi: 'शिकायत', en: 'Disputed' },
  'payouts.holdReason': { hi: 'क्यों रुका है', en: 'Why it is held' },

  // --- listings ----------------------------------------------------------
  'listings.title': { hi: 'आपकी साइट', en: 'Your sites' },
  'listings.add': { hi: 'नई साइट जोड़ें', en: 'Add a site' },
  'listings.empty': {
    hi: 'होर्डिंग या और मीडिया है? यहाँ जोड़ें, ग्राहक सीधे बुक करेंगे।',
    en: 'Have a hoarding or other media? List it here and customers book it directly.',
  },
  'listings.titleField': { hi: 'साइट का नाम', en: 'Site name' },
  'listings.mediaType': { hi: 'किस तरह की मीडिया', en: 'Media type' },
  'listings.size': { hi: 'साइज़ (फ़ीट)', en: 'Size (ft)' },
  'listings.rate': { hi: 'रेट (₹)', en: 'Rate (₹)' },
  'listings.rateUnit': { hi: 'किस हिसाब से', en: 'Per' },
  'listings.illuminated': { hi: 'लाइट लगी है', en: 'Illuminated' },
  'listings.impressions': { hi: 'रोज़ कितने लोग देखते हैं', en: 'People who see it daily' },
  'listings.impressionMethod': { hi: 'यह आँकड़ा कहाँ से', en: 'Where that number comes from' },
  'listings.impressionMethodHint': {
    hi: 'ग्राहक को यह साफ़ दिखेगा। अंदाज़ा है तो अंदाज़ा ही लिखें — गलत आँकड़ा पकड़ा जाता है।',
    en: 'Customers see this. If it is an estimate, say so — an inflated number gets found out.',
  },
  'listings.draft': { hi: 'ड्राफ़्ट', en: 'Draft' },
  'listings.live': { hi: 'चालू', en: 'Live' },
  'listings.paused': { hi: 'रोका हुआ', en: 'Paused' },
  'listings.bookedDays': { hi: 'दिन बुक', en: 'days booked' },
  'listings.pauseIt': { hi: 'रोक दें', en: 'Pause' },
  'listings.resumeIt': { hi: 'चालू करें', en: 'Make live' },

  // --- profile -----------------------------------------------------------
  'profile.title': { hi: 'प्रोफ़ाइल', en: 'Profile' },
  'profile.status': { hi: 'स्थिति', en: 'Status' },
  'profile.status.pending': { hi: 'मंज़ूरी बाकी', en: 'Awaiting approval' },
  'profile.status.verified': { hi: 'मंज़ूर', en: 'Verified' },
  'profile.status.active': { hi: 'चालू', en: 'Active' },
  'profile.status.paused': { hi: 'रोका हुआ', en: 'Paused' },
  'profile.status.suspended': { hi: 'बंद', en: 'Suspended' },
  'profile.jobsDone': { hi: 'पूरे किए काम', en: 'Jobs completed' },
  'profile.rating': { hi: 'रेटिंग', en: 'Rating' },
  'profile.ratingLocked': { hi: '5 काम के बाद', en: 'after 5 jobs' },
  'profile.commission': { hi: 'कमीशन', en: 'Commission' },
  'profile.paymentTerms': { hi: 'पेमेंट', en: 'Payment terms' },
  'profile.language': { hi: 'भाषा', en: 'Language' },
  'profile.signOut': { hi: 'साइन आउट', en: 'Sign out' },
  'profile.addPan': { hi: 'PAN जोड़ें', en: 'Add your PAN' },
} as const;

export type StringKey = keyof typeof STRINGS;

interface I18n {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (key: StringKey) => string;
}

const Ctx = createContext<I18n | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'hi' || stored === 'en') return stored;
    // Respect an explicitly English device, but default to Hindi otherwise —
    // including for the very common case of a phone left on its shipped
    // English locale by someone who does not read it.
    return navigator.language?.startsWith('en-GB') || navigator.language === 'en' ? 'en' : 'hi';
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, lang);
    document.documentElement.lang = lang;
  }, [lang]);

  const value = useMemo<I18n>(
    () => ({
      lang,
      setLang: setLangState,
      t: (key) => STRINGS[key]?.[lang] ?? STRINGS[key]?.en ?? key,
    }),
    [lang],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useI18n(): I18n {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useI18n outside I18nProvider');
  return ctx;
}

/** Capability labels, kept beside the dictionary they belong to. */
export const CAPABILITY_LABEL: Record<string, { hi: string; en: string }> = {
  ooh_hoarding: { hi: 'होर्डिंग', en: 'Hoarding' },
  ooh_society: { hi: 'सोसाइटी बोर्ड', en: 'Society boards' },
  ooh_transit: { hi: 'बस / ऑटो ब्रांडिंग', en: 'Transit media' },
  ooh_mobile_van: { hi: 'प्रचार वैन', en: 'Mobile van' },
  radio: { hi: 'रेडियो', en: 'Radio' },
  newspaper: { hi: 'अख़बार', en: 'Newspaper' },
  tv_cable: { hi: 'केबल टीवी', en: 'Cable TV' },
  cinema: { hi: 'सिनेमा', en: 'Cinema' },
  event_venue: { hi: 'इवेंट जगह', en: 'Event venue' },
  digital_screen: { hi: 'डिजिटल स्क्रीन', en: 'Digital screen' },
  print_flex: { hi: 'फ्लेक्स प्रिंटिंग', en: 'Flex printing' },
  print_offset: { hi: 'ऑफसेट प्रिंटिंग', en: 'Offset printing' },
  print_digital: { hi: 'डिजिटल प्रिंटिंग', en: 'Digital printing' },
  signage_fabrication: { hi: 'साइन बोर्ड बनाना', en: 'Signage fabrication' },
  installation: { hi: 'लगाने का काम', en: 'Installation' },
  photography: { hi: 'फ़ोटोग्राफ़ी', en: 'Photography' },
  videography: { hi: 'वीडियो', en: 'Videography' },
  voice_studio: { hi: 'वॉइस रिकॉर्डिंग', en: 'Voice studio' },
  event_management: { hi: 'इवेंट मैनेजमेंट', en: 'Event management' },
  distribution: { hi: 'पैम्फलेट बाँटना', en: 'Distribution' },
  creator: { hi: 'क्रिएटर / इन्फ़्लुएंसर', en: 'Creator / influencer' },
};

export function capabilityLabel(capability: string, lang: Lang): string {
  return CAPABILITY_LABEL[capability]?.[lang] ?? capability.replace(/_/g, ' ');
}

/**
 * For newer screens: the Hindi and the English side by side, where they are
 * used. `const T = useT(); T('बुकिंग', 'Bookings')`.
 */
export function useT(): (hi: string, en: string) => string {
  const { lang } = useI18n();
  return (hi, en) => (lang === 'hi' ? hi : en);
}
